import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import classnames from 'classnames/bind'
import {
  Icon,
  EnhancedDrawer,
  JsonBeforeAfter,
  InputFieldReduxFormWrapper as InputField,
} from '@adc/ui-core'
import Form from '../form'
import * as utils from '../../utils'
import useFieldChangeTimeline from '../../hooks/useFieldChangeTimeline'
import { auditHistoryDrawerContent } from '../../constants/tooltips'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const { isJSON, parseJSONData } = utils.json
const { date } = utils

// ─── Utilities ───────────────────────────────────────────────────────────────

/**
 * Groups flat FieldChangeTimeline[] into revisions by timestamp.
 * Returns [{ changed_at, changed_by, fields: [{ field, previous_value, current_value, delta_label }] }]
 */
const groupTimelinesByRevision = timelines => {
  const byTimestamp = {}
  for (const timeline of timelines) {
    for (const change of timeline.changes) {
      const key = change.changed_at
      if (!byTimestamp[key]) {
        byTimestamp[key] = {
          changed_at: change.changed_at,
          changed_by: change.changed_by,
          fieldMap: {},
        }
      }
      // Values come as JSON strings from the API — parse them
      let prevVal = change.previous_value
      let currVal = change.current_value
      try {
        prevVal = JSON.parse(prevVal)
      } catch (e) {
        /* keep as-is */
      }
      try {
        currVal = JSON.parse(currVal)
      } catch (e) {
        /* keep as-is */
      }

      const existing = byTimestamp[key].fieldMap[timeline.field]
      if (existing) {
        // Keep earliest previous, latest current (net change)
        existing.current_value = currVal
        existing.delta_label = change.delta_label
      } else {
        byTimestamp[key].fieldMap[timeline.field] = {
          field: timeline.field,
          previous_value: prevVal,
          current_value: currVal,
          delta_label: change.delta_label,
        }
      }
    }
  }
  return Object.values(byTimestamp)
    .map(rev => ({
      changed_at: rev.changed_at,
      changed_by: rev.changed_by,
      fields: Object.values(rev.fieldMap),
    }))
    .sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at))
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const isComplex = val =>
  val !== null && typeof val === 'object' && !Array.isArray(val)

const formatPrimitive = val => {
  if (val === null || val === undefined) return 'null'
  return String(val)
}

const FieldDiffLine = ({ f, variant }) => {
  const prevComplex = isComplex(f.previous_value)
  const currComplex = isComplex(f.current_value)

  if (prevComplex || currComplex) {
    return (
      <div className={cx('diff-line', `diff-line--${variant}`)}>
        <span className={cx('diff-key')}>{f.field}</span>
        <JsonBeforeAfter
          before={f.previous_value || {}}
          after={f.current_value || {}}
          beforeTitle='Before'
          afterTitle='After'
        />
      </div>
    )
  }

  if (variant === 'added') {
    return (
      <div className={cx('diff-line', 'diff-line--added')}>
        <span className={cx('diff-key')}>{f.field}:</span>
        <span className={cx('diff-value', 'diff-value--added')}>
          {formatPrimitive(f.current_value)}
        </span>
      </div>
    )
  }

  if (variant === 'removed') {
    return (
      <div className={cx('diff-line', 'diff-line--removed')}>
        <span className={cx('diff-key')}>{f.field}:</span>
        <span className={cx('diff-value', 'diff-value--removed')}>
          {formatPrimitive(f.previous_value)}
        </span>
      </div>
    )
  }

  return (
    <div className={cx('diff-line', 'diff-line--modified')}>
      <span className={cx('diff-key')}>{f.field}:</span>
      <span className={cx('diff-value', 'diff-value--old')}>
        {formatPrimitive(f.previous_value)}
      </span>
      <span className={cx('diff-arrow')}>&rarr;</span>
      <span className={cx('diff-value', 'diff-value--new')}>
        {formatPrimitive(f.current_value)}
      </span>
    </div>
  )
}

// Group fields by their top-level prefix (e.g. 'auction/end_date' → 'auction')
const groupByPrefix = fields => {
  const groups = {}
  for (const f of fields) {
    const slashIdx = f.field.indexOf('/')
    const group = slashIdx > 0 ? f.field.substring(0, slashIdx) : '_ungrouped'
    if (!groups[group]) groups[group] = []
    groups[group].push(f)
  }
  return groups
}

const FieldGroup = ({ groupName, fields }) => {
  if (groupName === '_ungrouped') {
    return fields.map(f => (
      <FieldDiffLine key={f.field} f={f} variant={getVariant(f)} />
    ))
  }
  return (
    <details className={cx('field-group')} open>
      <summary className={cx('field-group-header')}>
        {groupName} ({fields.length})
      </summary>
      {fields.map(f => (
        <FieldDiffLine key={f.field} f={f} variant={getVariant(f)} />
      ))}
    </details>
  )
}

const getVariant = f => {
  if (f.delta_label === 'added') return 'added'
  if (f.delta_label === 'removed') return 'removed'
  return 'modified'
}

export const UnifiedDiffView = ({ fields }) => {
  if (!fields || !fields.length) {
    return (
      <p className={cx('no-changes')}>No changes detected in this revision.</p>
    )
  }

  const groups = groupByPrefix(fields)

  return (
    <div className={cx('unified-diff')}>
      {Object.entries(groups).map(([group, gFields]) => (
        <FieldGroup key={group} groupName={group} fields={gFields} />
      ))}
    </div>
  )
}

// ─── Legacy single-field view ────────────────────────────────────────────────

export const DrawerTitle = ({ data }) => {
  const titleText = `${date.formatDateTimeApiToUi(data.updated_date)} -- ${
    data.updated_by
  }`
  return (
    <span>
      <Icon name='write' />
      <span data-elm-id='drawer_title'>{titleText}</span>
    </span>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

const AuditHistoryDrawer = ({
  open,
  onClose,
  data = {},
  entityType,
  entityId,
  timelines: timelinesProp,
  selectedIndex = 0,
}) => {
  const { timelines: timelinesFromHook } = useFieldChangeTimeline(
    timelinesProp ? undefined : entityType,
    timelinesProp ? undefined : entityId
  )
  const timelines = timelinesProp || timelinesFromHook
  const [currentIndex, setCurrentIndex] = useState(0)
  const [auditView, setAuditView] = useState('legacy')
  const ReactJsonRef = useRef(null)

  useEffect(() => {
    // react-json-view accesses `document` at init — only load client-side
    // eslint-disable-next-line global-require
    ReactJsonRef.current =
      require('react-json-view').default || require('react-json-view')
  }, [])

  const revisions = useMemo(
    () => groupTimelinesByRevision(timelines),
    [timelines]
  )

  useEffect(() => {
    if (open) {
      setCurrentIndex(selectedIndex || 0)
      setAuditView('legacy')
    }
  }, [open, selectedIndex])

  const navigatePrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(0, prev - 1))
  }, [])

  const navigateNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(revisions.length - 1, prev + 1))
  }, [revisions.length])

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const getInputProps = value => ({
    onChange: () => {},
    onBlur: () => {},
    value,
  })

  const renderJSON = payloadString => {
    if (!payloadString) return <p>No Data Found.</p>
    const payloadObject = parseJSONData(payloadString)
    const ReactJson = ReactJsonRef.current
    return payloadObject && ReactJson ? (
      <ReactJson src={payloadObject} displayDataTypes={false} />
    ) : (
      <p className={cx('error')}>
        {payloadObject ? 'Loading...' : 'Error: Unable to parse data.'}
      </p>
    )
  }

  const renderPayload = (payload, type) => (
    <div data-elm-id={type}>
      {!isJSON(payload) ? payload : renderJSON(payload)}
    </div>
  )

  // ─── View toggle ────────────────────────────────────────────────────────

  const renderViewToggle = () => (
    <div className={cx('view-toggle-bar')} data-elm-id='audit_view_toggle_bar'>
      <button
        className={cx('view-toggle-btn', {
          'view-toggle-btn--active': auditView === 'legacy',
        })}
        onClick={() => setAuditView('legacy')}
        type='button'
        data-elm-id='audit_toggle_field_view'
      >
        Field View
      </button>
      <button
        className={cx('view-toggle-btn', {
          'view-toggle-btn--active': auditView === 'compare',
        })}
        onClick={() => setAuditView('compare')}
        type='button'
        data-elm-id='audit_toggle_diff_view'
      >
        Diff View
      </button>
    </div>
  )

  // ─── Legacy mode ────────────────────────────────────────────────────────

  const renderLegacyMode = () => (
    <EnhancedDrawer
      open={open}
      onClose={onClose}
      headerContent={<DrawerTitle data={data} />}
      sidebarWidth={400}
      elmId='audit-history-drawer'
    >
      {revisions.length > 0 && renderViewToggle()}
      <div className={cx('content')}>
        <Form padded={false}>
          <Form.Content>
            <Form.Column colSize='full'>
              <Form.Section title='Update'>
                <InputField
                  name='updated_date'
                  label='Updated Date'
                  elmId='updated_date'
                  viewMode
                  breakAll
                  helpContent={auditHistoryDrawerContent.UPDATED_DATE}
                  showTooltip
                  input={getInputProps(
                    date.formatDateTimeApiToUi(data.updated_date)
                  )}
                />
                <InputField
                  name='updated_by'
                  label='Updated By'
                  elmId='updated_by'
                  viewMode
                  breakAll
                  helpContent={auditHistoryDrawerContent.UPDATED_BY}
                  showTooltip
                  input={getInputProps(data.updated_by)}
                />
                <InputField
                  name='field'
                  label='Field'
                  elmId='field'
                  viewMode
                  helpContent={auditHistoryDrawerContent.FIELD}
                  showTooltip
                  input={getInputProps((data.field || '').toUpperCase())}
                />
              </Form.Section>

              <Form.Section title='From'>
                {renderPayload(data.drawerFrom, 'from')}
              </Form.Section>
              <Form.Section title='To'>
                {renderPayload(data.drawerTo, 'to')}
              </Form.Section>
            </Form.Column>
          </Form.Content>
        </Form>
      </div>
    </EnhancedDrawer>
  )

  // ─── Compare mode ──────────────────────────────────────────────────────

  const renderCompareMode = () => {
    const revision = revisions[currentIndex]
    if (!revision) return renderLegacyMode()

    const { fields } = revision
    const added = fields.filter(f => f.delta_label === 'added')
    const removed = fields.filter(f => f.delta_label === 'removed')
    const modified = fields.filter(
      f =>
        f.delta_label &&
        f.delta_label !== 'added' &&
        f.delta_label !== 'removed'
    )

    const title = (
      <span>
        <Icon name='zoom' color='grayDark' />
        <span data-elm-id='json_compare_title' className={cx('drawer-title')}>
          JSON State Compare &mdash; Revision {currentIndex + 1} of{' '}
          {revisions.length}
        </span>
      </span>
    )

    return (
      <EnhancedDrawer
        open={open}
        onClose={onClose}
        headerContent={title}
        sidebarWidth={800}
        elmId='audit-history-drawer'
      >
        {revisions.length > 0 && renderViewToggle()}
        <div
          className={cx('compare-content')}
          data-elm-id='audit_compare_content'
        >
          <div
            className={cx('timestamp-bar')}
            data-elm-id='audit_timestamp_bar'
          >
            <span>
              {date.formatDateTimeApiToUi(revision.changed_at)} &mdash; User:{' '}
              {revision.changed_by || 'system'}
            </span>
            <span>
              {fields.length} field change{fields.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className={cx('nav-controls')} data-elm-id='audit_nav_controls'>
            <button
              className={cx('nav-button')}
              onClick={navigatePrev}
              disabled={currentIndex === 0}
              data-elm-id='audit_nav_newer'
            >
              &#8592; Newer
            </button>
            <span
              className={cx('nav-indicator')}
              data-elm-id='audit_nav_indicator'
            >
              {currentIndex + 1} / {revisions.length}
            </span>
            <button
              className={cx('nav-button')}
              onClick={navigateNext}
              disabled={currentIndex >= revisions.length - 1}
              data-elm-id='audit_nav_older'
            >
              Older &#8594;
            </button>
          </div>

          <div
            className={cx('change-summary')}
            data-elm-id='audit_change_summary'
          >
            <span className={cx('change-badge', 'change-badge--added')}>
              +{added.length} added
            </span>
            <span className={cx('change-badge', 'change-badge--modified')}>
              ~{modified.length} modified
            </span>
            <span className={cx('change-badge', 'change-badge--removed')}>
              -{removed.length} removed
            </span>
          </div>

          <UnifiedDiffView fields={fields} />
        </div>
      </EnhancedDrawer>
    )
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  if (auditView === 'compare') {
    return renderCompareMode()
  }
  return renderLegacyMode()
}

export default AuditHistoryDrawer
